import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  CurrencyInr, ChartLineUp, Factory, Lightning, Package, ShoppingCart, TrendUp,
  Warning, Trophy, Snowflake, Gauge
} from "@phosphor-icons/react";

const INR = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(n || 0));

function Metric({ label, value, sub, tone = "default", icon: Icon, prefix = "", suffix = "", testId, large = false }) {
  const t = {
    default: "text-foreground", good: "text-emerald-600", warn: "text-amber-600",
    bad: "text-red-600", primary: "text-primary",
  };
  return (
    <Card className="shadow-none" data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="overline">{label}</div>
          {Icon && <Icon size={16} className="text-muted-foreground"/>}
        </div>
        <div className={`text-metric ${large ? "text-4xl" : "text-3xl"} ${t[tone]}`}>
          {prefix}{value}{suffix && <span className="text-lg text-muted-foreground ml-1">{suffix}</span>}
        </div>
        {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

export default function Management() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/management/ceo-dashboard").then(r => setD(r.data)).catch(()=>{}); }, []);
  if (!d) return <div className="p-6 text-muted-foreground">Loading executive dashboard…</div>;

  const profitTone = d.today_profit_estimate >= 0 ? "good" : "bad";

  return (
    <div data-testid="management-page">
      <PageHeader
        subtitle="Chericious · Executive View"
        title="Management Dashboard"
        actions={<Badge variant="secondary" className="mono">{d.date}</Badge>}
      />

      {/* Today's headline */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Metric label="Today's Revenue" prefix="₹" value={INR(d.today_revenue)}
                icon={CurrencyInr} tone="primary" large sub="Sales orders billed today"
                testId="ceo-revenue"/>
        <Metric label="Today's Production" value={d.today_production_dry_kg} suffix="kg dry"
                icon={Factory} tone="good" large sub="Aggregated from batches today"
                testId="ceo-production"/>
        <Metric label="Estimated Mfg Cost" prefix="₹" value={INR(d.today_mfg_cost)}
                icon={ChartLineUp} sub="BOM + labour + machine + overhead"
                testId="ceo-mfg-cost"/>
        <Metric label="Profit Estimate" prefix="₹" value={INR(d.today_profit_estimate)}
                icon={TrendUp} tone={profitTone} large sub="Revenue − manufacturing cost"
                testId="ceo-profit"/>
      </div>

      {/* Efficiency & Utilisation */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Metric label="Machine Utilisation" value={d.machine_utilization_pct} suffix="%"
                icon={Snowflake} tone={d.machine_utilization_pct >= 70 ? "good" : d.machine_utilization_pct >= 40 ? "warn" : "bad"}
                sub="Both freeze dryers combined" testId="ceo-util"/>
        <Metric label="Production Efficiency" value={d.production_efficiency_pct} suffix="%"
                icon={Gauge} tone={d.production_efficiency_pct >= 90 ? "good" : d.production_efficiency_pct >= 70 ? "warn" : "bad"}
                sub="Actual dry vs planned dry" testId="ceo-efficiency"/>
        <Metric label="Power Consumption" value={d.power_kwh} suffix="kWh"
                icon={Lightning} tone="primary" sub="Today's freeze dryers"
                testId="ceo-power"/>
        <Metric label="Total Inventory Value" prefix="₹" value={INR(d.inventory.total_value)}
                icon={Package} sub={`FG ₹${INR(d.inventory.fg_value)} + Raw ₹${INR(d.inventory.raw_material_value)}`}
                testId="ceo-inventory-value"/>
      </div>

      {/* Pipeline value */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <Card className="shadow-none">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="overline flex items-center gap-1"><ShoppingCart size={13}/>Pending Sales Orders</div>
              <Badge variant="secondary">{d.pending.orders_count}</Badge>
            </div>
            <div className="text-metric text-3xl text-primary" data-testid="ceo-orders-value">₹{INR(d.pending.orders_value)}</div>
            <div className="text-xs text-muted-foreground mt-1">Awaiting picking / packing / dispatch</div>
          </CardContent>
        </Card>
        <Card className="shadow-none">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="overline flex items-center gap-1"><ShoppingCart size={13}/>Pending Purchase Orders</div>
              <Badge variant="secondary">{d.pending.purchases_count}</Badge>
            </div>
            <div className="text-metric text-3xl text-amber-600" data-testid="ceo-purchases-value">₹{INR(d.pending.purchases_value)}</div>
            <div className="text-xs text-muted-foreground mt-1">Materials on order</div>
          </CardContent>
        </Card>
      </div>

      {/* SKU highlights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="shadow-none border-emerald-200" data-testid="ceo-top-sku">
          <CardContent className="p-5">
            <div className="overline flex items-center gap-1 mb-1"><Trophy size={13} className="text-emerald-600"/>Top selling SKU · 30 days</div>
            {d.top_selling_sku ? (
              <>
                <div className="font-display font-bold text-lg leading-tight">{d.top_selling_sku.sku_name}</div>
                <div className="mono text-[11px] text-muted-foreground">{d.top_selling_sku.sku_code} · {d.top_selling_sku.category}</div>
                <div className="text-metric text-2xl text-emerald-600 mt-2">{d.top_selling_sku.velocity_30d}<span className="text-sm text-muted-foreground">/day</span></div>
              </>
            ) : <div className="text-xs text-muted-foreground italic">No sales in the last 30 days.</div>}
          </CardContent>
        </Card>
        <Card className="shadow-none border-red-200" data-testid="ceo-lowest-stock">
          <CardContent className="p-5">
            <div className="overline flex items-center gap-1 mb-1"><Warning size={13} className="text-red-600"/>Lowest stock SKU</div>
            {d.lowest_stock_sku ? (
              <>
                <div className="font-display font-bold text-lg leading-tight">{d.lowest_stock_sku.sku_name}</div>
                <div className="mono text-[11px] text-muted-foreground">{d.lowest_stock_sku.sku_code}</div>
                <div className="text-metric text-2xl text-red-600 mt-2">{d.lowest_stock_sku.days_of_cover}<span className="text-sm text-muted-foreground">d cover · {d.lowest_stock_sku.stock} pouches</span></div>
              </>
            ) : <div className="text-xs text-muted-foreground italic">No SKU at risk right now.</div>}
          </CardContent>
        </Card>
        <Card className="shadow-none" data-testid="ceo-slow-sku">
          <CardContent className="p-5">
            <div className="overline flex items-center gap-1 mb-1"><TrendUp size={13} className="text-sky-600 rotate-180"/>Slow-moving SKU</div>
            {d.slow_moving_sku ? (
              <>
                <div className="font-display font-bold text-lg leading-tight">{d.slow_moving_sku.sku_name}</div>
                <div className="mono text-[11px] text-muted-foreground">{d.slow_moving_sku.sku_code} · {d.slow_moving_sku.category}</div>
                <div className="text-metric text-2xl text-sky-600 mt-2">{d.slow_moving_sku.stock}<span className="text-sm text-muted-foreground"> pouches sitting</span></div>
              </>
            ) : <div className="text-xs text-muted-foreground italic">No slow-moving stock.</div>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
